import {createContext, useReducer, useContext, ReactNode} from 'react';

import {NodeKeyMap} from 'src/state/dependency2';

type ListDataType = {id: string, selected: boolean};
type Action = {type: 'updateSelectList', payload: {list: Array<ListDataType>, hiddenMap: NodeKeyMap | undefined}}
    | {type: 'setSelectList', payload: Array<ListDataType>}
    | {type: 'removeSelectList', payload: {hiddenMap: NodeKeyMap, nodeIds: Array<string>}}
    | {type: 'expandSelectList', payload: {hiddenMap: NodeKeyMap, nodeIds: Array<string>}}
type Dispatch = (action: Action) => void;
type SelectListState = Array<ListDataType>;
type ListProviderProps = {children: ReactNode};
type Reducer<State, Action> =
  (state: State, action: Action) => State;

const GraphContext = createContext<
  {selectList: SelectListState | undefined; selectDispatch: Dispatch} | undefined
  >(undefined);

function listReducer(selectList: SelectListState, action: Action) {
  switch (action.type) {
    case 'updateSelectList': {
      if (!selectList) break;
      const nodeIds: Array<string> = action.payload.list.map((item) => {
        return item.id;
      });
      const filteredSelectList = selectList.filter(({id}) => !nodeIds.includes(id));
      const newSelectList = action.payload.list.map((item) => {
        if (action.payload.hiddenMap) {
          item.selected = action.payload.hiddenMap[item.id] === true ||
          action.payload.hiddenMap[item.id] === undefined ? false : true;
        }
        return item;
      });
      return [...filteredSelectList, ...newSelectList];
    }
    case 'setSelectList': {
      return action.payload;
    }
    case 'removeSelectList': {
      const changeArray: Array<ListDataType> = selectList.filter(({id}) => action.payload.nodeIds.includes(id));
      const removeArray: Array<ListDataType> = selectList.filter(({id}) => !action.payload.nodeIds.includes(id));
      const newArray: Array<ListDataType> = changeArray.map((item) => {
        item.selected = false;
        return item;
      });
      return [...removeArray, ...newArray];
    }
    case 'expandSelectList': {
      const changeArray: Array<ListDataType> = selectList.filter(({id}) => action.payload.nodeIds.includes(id));
      const removeArray: Array<ListDataType> = selectList.filter(({id}) => !action.payload.nodeIds.includes(id));
      const newArray: Array<ListDataType> = changeArray.map((item) => {
        item.selected = true;
        return item;
      });
      return [...removeArray, ...newArray];
    }
    default: {
      throw new Error('Unhandled action type in context provider');
    }
  }
}

export function ListProvider({children}: ListProviderProps) {
  const [selectList, selectDispatch] = useReducer<Reducer<any, any>>(listReducer, []);
  // NOTE: you *might* need to memoize this value
  // Learn more in http://kcd.im/optimize-context
  const value = {selectList, selectDispatch};
  return (
    <GraphContext.Provider value={value}>
      {children}
    </GraphContext.Provider>
  );
}

export function useSelectList() {
  const context = useContext(GraphContext);
  const selectList = context?.selectList ? context.selectList : undefined;
  const selectDispatch = context?.selectDispatch ? context.selectDispatch : () => {};

  return {
    selectList,
    selectDispatch,
  };
}
